import * as THREE from 'three';

const WATERCRAFT_ITEM_IDS = new Set(['boat', 'bamboo_raft']);
const BOAT_FLOAT_OFFSET_Y = 0.08;
const BOAT_SEAT_HEAD_OFFSET = 1.08;
const BOAT_IDLE_FLOAT_INTERVAL = 0.22;
const BOAT_CLEARANCE_HEIGHT = 2;
const BOAT_OVERLAP_RADIUS = 1.12;

function roundBlock(value) {
  return Math.round(Number(value) || 0);
}

function normalizeAngle(angle) {
  let value = Number(angle) || 0;
  const fullTurn = Math.PI * 2;
  while (value <= -Math.PI) value += fullTurn;
  while (value > Math.PI) value -= fullTurn;
  return value;
}

function dampAngle(current, target, delta, speed = 14) {
  const t = Math.min(1, Math.max(0, Number(delta) || 0) * Math.max(0, speed));
  return normalizeAngle(current + (normalizeAngle(target - current) * t));
}

function makeMaterial(colorValue) {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(colorValue)
  });
}

function createBoatMesh(type = 'boat', itemDefs = {}) {
  const isRaft = type === 'bamboo_raft';
  const rootColor = itemDefs[type]?.color || (isRaft ? '#d6b86f' : '#9e7749');
  const trimColor = isRaft ? '#9d7d3d' : '#6a4728';
  const accentColor = isRaft ? '#f0d28a' : '#c89b68';

  const root = new THREE.Mesh(
    new THREE.BoxGeometry(isRaft ? 1.24 : 1.08, isRaft ? 0.12 : 0.18, isRaft ? 2.14 : 2.02),
    makeMaterial(rootColor)
  );
  root.castShadow = true;
  root.receiveShadow = true;

  const trimMaterial = makeMaterial(trimColor);
  const accentMaterial = makeMaterial(accentColor);

  if (isRaft) {
    for (let i = 0; i < 5; i += 1) {
      const slat = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.08, 2.08), trimMaterial);
      slat.position.set(-0.36 + (i * 0.18), 0.02, 0);
      root.add(slat);
    }

    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.08, 0.42), accentMaterial);
    seat.position.set(0, 0.11, 0.18);
    root.add(seat);

    const crossbar = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.05, 0.16), accentMaterial);
    crossbar.position.set(0, 0.07, -0.44);
    root.add(crossbar);
  } else {
    const leftRail = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.34, 1.96), trimMaterial);
    leftRail.position.set(-0.46, 0.14, 0);
    root.add(leftRail);

    const rightRail = leftRail.clone();
    rightRail.position.x = 0.46;
    root.add(rightRail);

    const bow = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.32, 0.18), trimMaterial);
    bow.position.set(0, 0.12, -0.92);
    root.add(bow);

    const stern = bow.clone();
    stern.position.z = 0.92;
    root.add(stern);

    const seatFront = new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.08, 0.38), accentMaterial);
    seatFront.position.set(0, 0.14, 0.24);
    root.add(seatFront);

    const seatRear = seatFront.clone();
    seatRear.position.z = -0.2;
    root.add(seatRear);
  }

  return root;
}

export function createBoatSystem({
  scene,
  camera,
  controls,
  world,
  player,
  ui,
  itemDefs = {},
  isCreativeMode = () => false,
  sounds = null,
  heldItem = null,
  findSafeStandPosition = null
} = {}) {
  const boats = new Map();
  const lookDir = new THREE.Vector3();
  let nextBoatId = 1;
  let mountedBoatId = null;
  let idleFloatTimer = 0;

  function getControlObject() {
    return controls?.getObject?.() || null;
  }

  function getMountedBoat() {
    return mountedBoatId === null ? null : (boats.get(mountedBoatId) || null);
  }

  function getLookYaw(fallback = 0) {
    if (!camera?.getWorldDirection) return normalizeAngle(fallback);
    camera.getWorldDirection(lookDir);
    lookDir.y = 0;
    if (lookDir.lengthSq() <= 1e-8) return normalizeAngle(fallback);
    lookDir.normalize();
    return Math.atan2(lookDir.x, lookDir.z);
  }

  function getBoatFromHit(hit = null) {
    const boatId = Number(hit?.object?.userData?.boatId);
    if (!Number.isFinite(boatId)) return null;
    return boats.get(Math.floor(boatId)) || null;
  }

  function findNearbyWaterCell(x, y, z) {
    const offsets = [
      [0, 0, 0],
      [0, -1, 0],
      [0, 1, 0],
      [1, 0, 0],
      [-1, 0, 0],
      [0, 0, 1],
      [0, 0, -1],
      [1, -1, 0],
      [-1, -1, 0],
      [0, -1, 1],
      [0, -1, -1],
      [1, 0, 1],
      [1, 0, -1],
      [-1, 0, 1],
      [-1, 0, -1]
    ];
    for (const [dx, dy, dz] of offsets) {
      const cx = x + dx;
      const cy = y + dy;
      const cz = z + dz;
      if (world?.isWaterAt?.(cx, cy, cz)) return { x: cx, y: cy, z: cz };
    }
    return null;
  }

  function hasBoatOverlapAt(x, y, z) {
    for (const boat of boats.values()) {
      const dx = boat.mesh.position.x - x;
      const dy = boat.mesh.position.y - y;
      const dz = boat.mesh.position.z - z;
      if ((dx * dx) + (dz * dz) > (BOAT_OVERLAP_RADIUS * BOAT_OVERLAP_RADIUS)) continue;
      if (Math.abs(dy) <= 1.2) return true;
    }
    return false;
  }

  function hasPlacementClearance(x, y, z) {
    for (let dy = 1; dy <= BOAT_CLEARANCE_HEIGHT; dy += 1) {
      if (world?.isSolidForCollisionAt?.(x, y + dy, z)) return false;
    }
    return true;
  }

  function findPlacementCell(hit = null) {
    if (!hit?.object?.position) return null;
    const candidates = [];
    const seen = new Set();
    const pushCandidate = (x, y, z) => {
      const key = `${x},${y},${z}`;
      if (seen.has(key)) return;
      seen.add(key);
      candidates.push({ x, y, z });
    };

    const baseX = roundBlock(hit.object.position.x);
    const baseY = roundBlock(hit.object.position.y);
    const baseZ = roundBlock(hit.object.position.z);
    const normalX = roundBlock(hit?.face?.normal?.x);
    const normalY = roundBlock(hit?.face?.normal?.y);
    const normalZ = roundBlock(hit?.face?.normal?.z);

    pushCandidate(baseX + normalX, baseY + normalY, baseZ + normalZ);
    pushCandidate(baseX, baseY, baseZ);
    pushCandidate(baseX, baseY - 1, baseZ);

    if (hit?.point) {
      pushCandidate(roundBlock(hit.point.x), roundBlock(hit.point.y), roundBlock(hit.point.z));
    }

    const horizontalOffsets = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1]
    ];
    for (const [dx, dz] of horizontalOffsets) {
      pushCandidate(baseX + dx, baseY, baseZ + dz);
      pushCandidate(baseX + dx, baseY - 1, baseZ + dz);
    }

    for (const candidate of candidates) {
      const waterCell = findNearbyWaterCell(candidate.x, candidate.y, candidate.z);
      if (!waterCell) continue;
      const floatY = waterCell.y + BOAT_FLOAT_OFFSET_Y;
      if (hasBoatOverlapAt(waterCell.x, floatY, waterCell.z)) continue;
      if (!hasPlacementClearance(waterCell.x, waterCell.y, waterCell.z)) continue;
      return waterCell;
    }

    return null;
  }

  function registerBoatMesh(mesh) {
    if (!mesh) return;
    world?.registerDynamicRaycastObject?.(mesh);
  }

  function unregisterBoatMesh(mesh) {
    if (!mesh) return;
    world?.unregisterDynamicRaycastObject?.(mesh);
  }

  function createBoatEntity(type, position, yaw = 0) {
    if (!WATERCRAFT_ITEM_IDS.has(type) || !position) return null;

    const id = nextBoatId++;
    const mesh = createBoatMesh(type, itemDefs);
    mesh.position.set(position.x, position.y, position.z);
    mesh.rotation.y = normalizeAngle(yaw);
    mesh.userData = {
      boatEntity: true,
      boatId: id,
      boatType: type,
      raycast: true,
      mineable: false,
      solid: false
    };

    scene?.add?.(mesh);
    registerBoatMesh(mesh);

    const boat = { id, type, mesh };
    boats.set(id, boat);
    return boat;
  }

  function removeBoatEntity(id, { preservePlayerMode = false } = {}) {
    const boat = boats.get(id);
    if (!boat) return false;

    if (mountedBoatId === id) mountedBoatId = null;
    unregisterBoatMesh(boat.mesh);
    if (boat.mesh.parent) boat.mesh.parent.remove(boat.mesh);
    else scene?.remove?.(boat.mesh);
    boats.delete(id);

    if (!preservePlayerMode) player?.setWatercraftMode?.(null);
    return true;
  }

  function syncMountedBoat(boat, delta = 0) {
    const controlObject = getControlObject();
    if (!boat || !controlObject?.position) return;

    boat.mesh.position.set(
      controlObject.position.x,
      controlObject.position.y - BOAT_SEAT_HEAD_OFFSET,
      controlObject.position.z
    );
    boat.mesh.rotation.y = dampAngle(
      boat.mesh.rotation.y,
      getLookYaw(boat.mesh.rotation.y),
      delta,
      16
    );
  }

  function findDismountPosition(boat) {
    const safe = findSafeStandPosition?.(
      roundBlock(boat.mesh.position.x),
      roundBlock(boat.mesh.position.y),
      roundBlock(boat.mesh.position.z)
    );
    if (!safe) return null;

    const sx = Number(safe.x);
    const sy = Number(safe.y);
    const sz = Number(safe.z);
    if (!Number.isFinite(sx) || !Number.isFinite(sy) || !Number.isFinite(sz)) return null;
    return new THREE.Vector3(sx, sy, sz);
  }

  function mountBoat(boat) {
    if (!boat) return false;
    if (mountedBoatId !== null && mountedBoatId !== boat.id) {
      dismountMountedBoat({ skipReposition: true, silent: true });
    }

    const controlObject = getControlObject();
    if (!controlObject?.position) return false;

    mountedBoatId = boat.id;
    controlObject.position.set(
      boat.mesh.position.x,
      boat.mesh.position.y + BOAT_SEAT_HEAD_OFFSET,
      boat.mesh.position.z
    );
    player?.setWatercraftMode?.(boat.type === 'bamboo_raft' ? 'bamboo_raft' : 'boat');
    heldItem?.triggerSwing?.(0.22);
    return true;
  }

  function dismountMountedBoat({ skipReposition = false, silent = false } = {}) {
    const boat = getMountedBoat();
    if (!boat) {
      mountedBoatId = null;
      player?.setWatercraftMode?.(null);
      return false;
    }

    mountedBoatId = null;
    player?.setWatercraftMode?.(null);

    if (!skipReposition) {
      const safe = findDismountPosition(boat);
      const controlObject = getControlObject();
      if (safe && controlObject?.position) {
        controlObject.position.copy(safe);
      }
    }

    if (!silent) heldItem?.triggerSwing?.(0.16);
    return true;
  }

  function updateIdleFloat() {
    for (const boat of boats.values()) {
      if (boat.id === mountedBoatId) continue;
      const waterCell = findNearbyWaterCell(
        roundBlock(boat.mesh.position.x),
        roundBlock(boat.mesh.position.y),
        roundBlock(boat.mesh.position.z)
      );
      if (!waterCell) continue;
      if (!hasPlacementClearance(waterCell.x, waterCell.y, waterCell.z)) continue;
      boat.mesh.position.y = waterCell.y + BOAT_FLOAT_OFFSET_Y;
    }
  }

  function tryUseBoatByHit(hit = null) {
    const targetedBoat = getBoatFromHit(hit);
    if (targetedBoat) {
      if (mountedBoatId === targetedBoat.id) return dismountMountedBoat();
      return mountBoat(targetedBoat);
    }

    const activeItemId = ui?.getActiveItemId?.() || null;
    if (!WATERCRAFT_ITEM_IDS.has(activeItemId) || (ui?.getActiveItemCount?.() || 0) <= 0) return false;

    const placementCell = findPlacementCell(hit);
    if (!placementCell) return false;

    const boat = createBoatEntity(activeItemId, {
      x: placementCell.x,
      y: placementCell.y + BOAT_FLOAT_OFFSET_Y,
      z: placementCell.z
    }, getLookYaw());
    if (!boat) return false;

    if (!isCreativeMode()) {
      if (!ui?.consumeActiveItem?.(1)) {
        removeBoatEntity(boat.id, { preservePlayerMode: true });
        return false;
      }
    }

    sounds?.placeByBlockId?.(6);
    heldItem?.triggerSwing?.(0.32);
    return true;
  }

  function tryBreakBoatByHit(hit = null) {
    const boat = getBoatFromHit(hit);
    if (!boat || mountedBoatId === boat.id) return false;

    const boatType = boat.type;
    const px = boat.mesh.position.x;
    const py = boat.mesh.position.y;
    const pz = boat.mesh.position.z;
    removeBoatEntity(boat.id, { preservePlayerMode: true });

    if (!isCreativeMode()) {
      world?.spawnDrop?.(px, py + 0.18, pz, boatType, {
        kind: 'item',
        color: itemDefs?.[boatType]?.color,
        count: 1
      });
    }
    sounds?.break?.(6);
    return true;
  }

  function clearAll({ keepPlayerMode = false } = {}) {
    const ids = Array.from(boats.keys());
    for (const id of ids) removeBoatEntity(id, { preservePlayerMode: true });
    mountedBoatId = null;
    if (!keepPlayerMode) player?.setWatercraftMode?.(null);
  }

  function exportState() {
    return Array.from(boats.values()).map((boat) => ({
      type: boat.type,
      x: boat.mesh.position.x,
      y: boat.mesh.position.y,
      z: boat.mesh.position.z,
      yaw: boat.mesh.rotation.y
    }));
  }

  function importState(snapshot = null) {
    clearAll();
    const entries = Array.isArray(snapshot)
      ? snapshot
      : (Array.isArray(snapshot?.boats) ? snapshot.boats : []);

    for (const entry of entries) {
      const type = String(entry?.type || '');
      if (!WATERCRAFT_ITEM_IDS.has(type)) continue;
      const x = Number(entry?.x);
      const y = Number(entry?.y);
      const z = Number(entry?.z);
      const yaw = Number(entry?.yaw);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
      createBoatEntity(type, { x, y, z }, Number.isFinite(yaw) ? yaw : 0);
    }
    return boats.size;
  }

  function update(delta = 0) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    const mountedBoat = getMountedBoat();
    if (mountedBoat) {
      syncMountedBoat(mountedBoat, safeDelta);
      if (player?.isCrouching?.()) {
        dismountMountedBoat();
      }
    }

    idleFloatTimer += safeDelta;
    if (idleFloatTimer >= BOAT_IDLE_FLOAT_INTERVAL) {
      idleFloatTimer = 0;
      updateIdleFloat();
    }
  }

  return {
    update,
    clearAll,
    exportState,
    importState,
    tryUseBoatByHit,
    tryBreakBoatByHit,
    dismountMountedBoat,
    isMounted() {
      return mountedBoatId !== null;
    },
    getMountedBoatId() {
      return mountedBoatId;
    },
    getBoatCount() {
      return boats.size;
    }
  };
}
